import { useState } from 'react';
import { ThreadListPrimitive, ThreadListItemPrimitive } from '@assistant-ui/react';
import { useIvyChatLayoutStore } from '@/stores/ivyChatLayoutStore';
import { useIvyChatThemeStore } from '@/stores/ivyChatThemeStore';
import {
  Wand2, Plus, Settings, Search, X, Sun, Moon,
  MessageSquare,
  PanelLeftClose, PanelLeft,
  Trash2,
} from 'lucide-react';

export function IvySidebar({ onOpenSettings }: { onOpenSettings: () => void }) {
  const collapsed = useIvyChatLayoutStore(s => s.sidebarCollapsed);
  const toggleSidebar = useIvyChatLayoutStore(s => s.toggleSidebar);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="flex h-full">
      {/* Icon Rail -- always visible */}
      <div className="w-[58px] flex-shrink-0 border-r border-border-secondary bg-surface-secondary flex flex-col items-center py-3 gap-2">
        {/* Logo */}
        <div className="w-9 h-9 rounded-xl bg-brand/10 flex items-center justify-center mb-2">
          <Wand2 className="w-5 h-5 text-brand" />
        </div>

        {/* New chat */}
        <ThreadListPrimitive.New asChild>
          <button className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="New Chat">
            <Plus className="w-5 h-5" />
          </button>
        </ThreadListPrimitive.New>

        {/* Collapse toggle */}
        <button
          onClick={toggleSidebar}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>

        <div className="flex-1" />

        {/* Theme toggle */}
        <ThemeToggle />

        {/* Settings */}
        <button
          onClick={onOpenSettings}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Model key settings"
        >
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {/* Conversation List -- collapsible */}
      {!collapsed && (
        <div className="w-[260px] flex-shrink-0 border-r border-border-secondary bg-background flex flex-col sidebar-transition">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border-secondary">
            <h2 className="text-sm font-semibold text-foreground">Conversations</h2>
          </div>

          {/* Search */}
          <div className="px-3 py-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-muted/50 border border-border-secondary rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-border transition-colors"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <ThreadListPrimitive.Items
              components={{
                ThreadListItem: IvyThreadListItem,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ThemeToggle() {
  const resolved = useIvyChatThemeStore(s => s.resolved);
  const toggle = useIvyChatThemeStore(s => s.toggle);

  return (
    <button
      onClick={toggle}
      className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      title={resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {resolved === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

function IvyThreadListItem() {
  return (
    <ThreadListItemPrimitive.Root className="group flex items-center px-3 py-2.5 cursor-pointer hover:bg-muted/50 data-[active]:bg-muted transition-colors border-b border-border-secondary/50">
      <ThreadListItemPrimitive.Trigger className="flex-1 min-w-0 flex items-center gap-2.5 text-left">
        <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 group-data-[active]:bg-brand/10">
          <MessageSquare className="w-3.5 h-3.5 text-muted-foreground group-data-[active]:text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm text-foreground line-clamp-1 group-data-[active]:font-medium">
            <ThreadListItemPrimitive.Title fallback="New Chat" />
          </span>
        </div>
      </ThreadListItemPrimitive.Trigger>

      <ThreadListItemPrimitive.Delete asChild>
        <button className="p-1 rounded text-muted-foreground/30 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </ThreadListItemPrimitive.Delete>
    </ThreadListItemPrimitive.Root>
  );
}
